import React, { useRef } from 'react';
import { AttachIcon, SendIcon } from '../icons';
import { UI_CONSTANTS } from '../../../constants';
import { useAutoResize } from '../../../hooks';

const InputBar = ({ 
  value, 
  onChange, 
  onKeyDown, 
  onSend, 
  onFileSelect, 
  disabled,
  placeholder = 'Message Doc...',
  showFileUpload = true,
  acceptedFileTypes = '', // MIME types or extensions to accept
  uploadingFileCount = 0, // Current number of files being uploaded
  isSending = false, // Additional loading state for send button
  isResponseGenerating = false, // Loading state for AI response
}) => {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  useAutoResize(textareaRef, value);

  /**
   * Determine if send button should be disabled
   * Disabled when:
   * - General disabled prop is true
   * - Message is empty/whitespace only
   * - Currently sending a message
   * - Waiting for AI response
   */
  const isSendDisabled = disabled || !value.trim() || isSending || isResponseGenerating;

  /**
   * Trigger file input click safely
   * WHY: Ensures file input ref exists before accessing it
   */
  const handleAttachClick = () => {
    try {
      fileInputRef.current?.click();
    } catch (err) {
      console.error('Error triggering file input:', err);
    }
  };

  /**
   * Handle file selection with error handling
   * WHY: Prevents crashes from unexpected file input states
   */
  const handleFileChange = (e) => {
    try {
      const files = e.target.files;
      // WHY: Check for files existence and non-zero length
      if (files && files.length > 0) {
        // Pass entire FileList to parent - let useFileUpload handle validation
        // WHY: Centralized validation logic prevents duplicate checks
        onFileSelect(e);
        // Reset input value so same file can be selected again if needed
        e.target.value = '';
      }
    } catch (err) {
      // WHY: Catch errors to prevent UI crash
      console.error('Error handling file selection:', err);
    }
  };

  return (
    <div className="input-bar">
      {showFileUpload && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFileTypes || '*'} // Allow all files if not specified
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            aria-hidden="true"
            disabled={disabled}
          />
          <button
            onClick={handleAttachClick}
            className="attach-button"
            disabled={disabled}
            aria-label="Attach files"
            title="Attach files (images, documents, spreadsheets, or data files)"
          >
            <AttachIcon size={UI_CONSTANTS.ICON_SIZE} />
          </button>
        </>
      )}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className="message-input"
        disabled={disabled}
        rows={1}
        aria-label="Message input"
      />
      <button
        onClick={onSend}
        className="send-button"
        disabled={isSendDisabled}
        aria-label="Send message"
        title={isSendDisabled ? (isSending || isResponseGenerating ? 'Waiting for response...' : 'Enter a message to send') : 'Send message'}
      >
        <SendIcon size={UI_CONSTANTS.ICON_SIZE} />
      </button>
    </div>
  );
};

export default InputBar;
